import React, {
  useCallback,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { base_URL } from "../../config/config";
import axios from "axios";
import { useParams } from "react-router-dom";
import DashboardCards from "./components/dashboard-cards";
import EnquiryTable from "./components/enquiry-table";
import Pagination from "./components/pagination";
export const useEnquiries = ({
  vendorId,
  page,
  pageSize,
  venueName,
  date,
  status,
}) => {
  return useQuery({
    queryKey: [
      "enquiriesData",
      vendorId,
      page,
      pageSize,
      venueName,
      date,
      status,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(venueName && { venueName }),
        ...(date && { date }),
        ...(status && status !== "All" && { status }),
      });

      const { data } = await axios.get(
        `${base_URL}/vendor-enquiries/${vendorId}?${params}`
      );
      return data;
    },
    keepPreviousData: true,
  });
};
export default function VendorEnquiries({
  setActiveEnquiryDetails,
  activeEnquiryDetails,
  user,
}){
  // Enquiry for dropdpwn state
  const [selectedOption, setSelectedOption] = useState("");
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [selectedDate, setSelectedDate] = useState("");
  // context
  // const { selectedEnquiryDetails, setSelectedEnquiryDetails } = useUser();
  const queryClient = useQueryClient();
  // for a team member invitedBy will be used
  const invitedBy = localStorage.getItem("invitedBy");
  // const vendorId = invitedBy || localStorage.getItem("vendorId");
  const { vendorId } = useParams();
  const [activeTab, setActiveTab] = useState("All");


  // Fetch New enquiries (status "Not Accepted" || 'New)
  const { data: newEnquiries, isLoading: loadingNewEnquiries } = useQuery({
    queryKey: ["newEnquiries", vendorId],
    queryFn: async () => {
      const response = await axios.get(
        `${base_URL}/api/vendor-enquiries/new/${vendorId}`
      );
      return response.data;
    },
    enabled: !!vendorId,
  });

  // Fetch Successful enquiries
  const { data: successfulEnquiries, isLoading: loadingSuccessfulEnquiries } =
    useQuery({
      queryKey: ["successfulEnquiries", vendorId],
      queryFn: async () => {
        const response = await axios.get(
          `${base_URL}/api/vendor-enquiries/successful/${vendorId}`
        );
        return response.data;
      },
      enabled: !!vendorId,
    });

  // Fetch Open Enquiries
  const { data: openEnquiries, isLoading: loadingOpenEnquiries } = useQuery({
    queryKey: ["openEnquiries", vendorId],
    queryFn: async () => {
      const response = await axios.get(
        `${base_URL}/api/vendor-enquiries/open/${vendorId}`
      );
      return response.data;
    },
    enabled: !!vendorId,
  });

  // Fetch Lost Enquiries
  const { data: lostEnquiries, isLoading: loadinglostEnquiries } = useQuery({
    queryKey: ["lostEnquiries", vendorId],
    queryFn: async () => {
      const response = await axios.get(
        `${base_URL}/api/vendor-enquiries/lost/${vendorId}`
      );
      return response.data;
    },
    enabled: !!vendorId,
  });
  // Fetch all Venue name
  const { data: venueNames } = useQuery({
    queryKey: ["venueNames", vendorId],
    queryFn: async () => {
      const response = await axios.get(`${base_URL}/api/enquiries/venues`, {
        params: { vendorId },
      });
      return response?.data;
    },
    enabled: !!vendorId,
  });

  // Status Update
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus, statusMessage, vendorEmail }) => {
      const response = await axios.patch(
        `${base_URL}/api/vendor-enquiries/${id}/status`,
        { status: newStatus, statusMessage, vendorEmail }
      );
      return response.data;
    },

    onMutate: async ({ id, newStatus }) => {
      await queryClient.cancelQueries({ queryKey: ["enquiriesData", vendorId] });

      // Snapshot all paginated variants so we can roll back on error
      const previousQueries = queryClient.getQueriesData({
        queryKey: ["enquiriesData", vendorId],
      });

      // Optimistically update every paginated variant of the table
      queryClient.setQueriesData(
        { queryKey: ["enquiriesData", vendorId] },
        (old) => {
          if (!old?.enquiries) return old;
          return {
            ...old,
            enquiries: old.enquiries.map((e) =>
              e._id === id ? { ...e, status: newStatus } : e
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (err, variables, context) => {
      context?.previousQueries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["vendorEnquiries", vendorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["successfulEnquiries", vendorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["newEnquiries", vendorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["openEnquiries", vendorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["lostEnquiries", vendorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vendorMetrics", vendorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["enquiriesData", vendorId],
      });
    },
  });

  // Function to update status
  const updateStatus = useCallback(
    (id, newStatus, statusMessage) => {
      if (!id) {
        console.error("Invalid enquiry ID:", id);
        return;
      }

      updateStatusMutation.mutate({
        id,
        newStatus,
        statusMessage,
        vendorEmail: user?.email,
      });
    },
    [updateStatusMutation, user?.email]
  );

  const { data: enquiriesData, isLoading: enquiriesLoading } = useEnquiries({
    vendorId,
    page: currentPage,
    pageSize,
    venueName: selectedVenue,
    date: selectedDate,
    status: activeTab,
  });

  const tabs = [
    { label: "All", count: enquiriesData?.statusCounts?.total || 0 },
    { label: "New", count: newEnquiries?.length || 0 },
    { label: "Open", count: openEnquiries?.length || 0 },
    {
      label: "Successful",
      count: successfulEnquiries?.length || 0,
    },
    { label: "Lost", count: lostEnquiries?.length || 0 },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleTabChange = (tabLabel) => {
    setActiveTab(tabLabel);
    setCurrentPage(1);
  };

  // Function to handle date change
  const handleDateChange = (e) => {
    const rawDate = e.target.value; // The original YYYY-MM-DD format
    setSelectedDate(rawDate); // Store the raw date in state
    setCurrentPage(1);
  };

  const handleVenueChange = (e) => {
    setSelectedVenue(e.target.value);
    setCurrentPage(1);
  };
  const handleClickArrow = async (enquiry) => {
    if (enquiry?.status === "Not Accepted") {
      // Update status to Active (Which means Ongoing)
      await updateStatus(enquiry?._id, "Active", `Active by ${user?.email}`);
      const updatedEnquiry = { ...enquiry, status: "Active" };
      // setSelectedEnquiryDetails(updatedEnquiry);
      setActiveEnquiryDetails(true);
      return;
    }
    if (enquiry?.status !== "Not Accepted" || enquiry?.status !== "New") {
      // setSelectedEnquiryDetails(enquiry);
      setActiveEnquiryDetails(true);
      return;
    }
    // Trigger the status update
    queryClient.setQueryData(["activeEnquiry"], {
      id: enquiry?._id,
      status: "Active",
      message: `Active by ${user?.email}`,
    });

    updateStatus(enquiry?._id, "Active", `Active by ${user?.email}`);
    setActiveEnquiryDetails(true);
    // setSelectedEnquiryDetails(enquiry);
  };

  return (
    <div className="w-full px-6 py-4">
      <div className=" mx-auto space-y-6">
        <DashboardCards />

        {/* Filters */}
        <div className="flex-wrap gap-4 items-center hidden md:flex">
          {tabs?.map((tab) => (
            <button
              key={tab.label}
              onClick={() => handleTabChange(tab.label)}
              className={`px-4 py-2 text-[14px] font-normal ${activeTab === tab.label
                ? "text-[#FE4747] border-b-2 border-[#FE4747]"
                : "text-[#222222]"
                }`}
            >
              {`${tab.label} (${tab.count})`}
            </button>
          ))}

          <div className="ml-auto flex gap-4">
            <select
              value={selectedVenue}
              onChange={handleVenueChange}
              className="border border-[#B0B0B0] text-[#B0B0B0] rounded-lg px-4 py-2 pr-6 outline-none text-sm w-full md:w-[197px] appearance-none bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M0.174019%200.688233L0.17402%200.688232C0.281236%200.571268%200.42254%200.512531%200.599191%200.512531H9.40081C9.57742%200.512531%209.71871%200.571814%209.82594%200.689912C9.93375%200.808653%209.9875%200.946829%209.9875%201.10499C9.9875%201.11278%209.9843%201.12734%209.97655%201.14987C9.96895%201.17194%209.95748%201.20035%209.94204%201.2352C9.91131%201.30453%209.8652%201.39862%209.80364%201.51756L5.62731%205.69389C5.53216%205.78904%205.43416%205.85814%205.33338%205.90159C5.23256%205.94505%205.12149%205.96687%205%205.96687C4.87851%205.96687%204.76744%205.94505%204.66662%205.90159C4.56584%205.85814%204.46785%205.78903%204.37269%205.69385L0.194725%201.51592C0.138084%201.45928%200.0935546%201.39612%200.0610483%201.32643C0.0287014%201.25706%200.0125%201.18271%200.0125%201.10323C0.0125%200.9439%200.0662612%200.805766%200.174019%200.688233Z%22%20fill%3D%22%23222222%22%20stroke%3D%22%23222222%22%20stroke-width%3D%220.025%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_1rem]"
            >
              <option value="" className="text-[#B0B0B0]">
                Enquiry for
              </option>
              {venueNames?.venues?.map((venue, index) => (
                <option key={index} value={venue}>
                  {venue}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="px-2 border border-[#B0B0B0] rounded-lg appearance-none outline-none h-[40px] md:w-[253px] bg-white text-[#B0B0B0] "
            />
          </div>
        </div>

        {/* Table */}
        {enquiriesLoading && (
          <div className="h-[40vh] mt-[5px] rounded-lg animate-pulse bg-black/20"></div>
        )}
        {
          !enquiriesLoading && (
            <>
              <EnquiryTable results={enquiriesData?.enquiries ?? []} updateStatus={updateStatus} currentPage={currentPage} pageSize={pageSize}>
                <Pagination currentPage={currentPage} pageSize={pageSize} handlePageChange={handlePageChange} handlePageSizeChange={handlePageSizeChange} totalCount={enquiriesData?.totalPages}/>
              </EnquiryTable>
            </>

          )
        }
      </div>
    </div>
  );
};